"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface VehicleInfoFormProps {
  onNext: (data: VehicleInfoData) => void;
}

export interface VehicleInfoData {
  vin: string;
  make: string;
  model: string;
  year: string;
  color: string;
  licensePlate: string;
}

export default function VehicleInfoForm({ onNext }: VehicleInfoFormProps) {
  const [formData, setFormData] = useState<VehicleInfoData>({
    vin: "",
    make: "",
    model: "",
    year: "",
    color: "",
    licensePlate: "",
  });

  const [errors, setErrors] = useState<Partial<VehicleInfoData>>({});

  const handleChange = (field: keyof VehicleInfoData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<VehicleInfoData> = {};

    if (!formData.vin || formData.vin.length !== 17) {
      newErrors.vin = "VIN må være nøyaktig 17 tegn";
    }
    if (!formData.make) newErrors.make = "Merke er påkrevd";
    if (!formData.model) newErrors.model = "Modell er påkrevd";
    if (!formData.year || parseInt(formData.year) < 1900 || parseInt(formData.year) > 2026) {
      newErrors.year = "Skriv inn et gyldig år (1900-2026)";
    }
    if (!formData.color) newErrors.color = "Farge er påkrevd";
    if (!formData.licensePlate) newErrors.licensePlate = "Registreringsnummer er påkrevd";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Kjøretøyidentifikasjonsnummer (VIN) *
        </label>
        <div className="relative">
          <input
            type="text"
            value={formData.vin}
            onChange={(e) => handleChange("vin", e.target.value.toUpperCase())}
            maxLength={17}
            placeholder="Skriv inn 17-tegns VIN"
            className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
              errors.vin ? "border-red-600" : "border-gray-300 hover:border-gray-400"
            }`}
          />
          {formData.vin && formData.vin.length === 17 && (
            <Icon
              name="CheckCircleIcon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600"
              size={20}
            />
          )}
        </div>
        {errors.vin && (
          <p className="mt-2 text-sm text-red-600 font-medium">{errors.vin}</p>
        )}
        <p className="mt-2 text-xs text-gray-500">
          17-tegns unik identifikator (bokstaver og tall)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Merke *
          </label>
          <input
            type="text"
            value={formData.make}
            onChange={(e) => handleChange("make", e.target.value)}
            placeholder="f.eks. Toyota"
            className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
              errors.make ? "border-red-600" : "border-gray-300 hover:border-gray-400"
            }`}
          />
          {errors.make && (
            <p className="mt-2 text-sm text-red-600 font-medium">{errors.make}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Modell *
          </label>
          <input
            type="text"
            value={formData.model}
            onChange={(e) => handleChange("model", e.target.value)}
            placeholder="f.eks. Camry"
            className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
              errors.model ? "border-red-600" : "border-gray-300 hover:border-gray-400"
            }`}
          />
          {errors.model && (
            <p className="mt-2 text-sm text-red-600 font-medium">{errors.model}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            År *
          </label>
          <input
            type="number"
            value={formData.year}
            onChange={(e) => handleChange("year", e.target.value)}
            placeholder="f.eks. 2024"
            min="1900"
            max="2026"
            className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
              errors.year ? "border-red-600" : "border-gray-300 hover:border-gray-400"
            }`}
          />
          {errors.year && (
            <p className="mt-2 text-sm text-red-600 font-medium">{errors.year}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Farge *
          </label>
          <input
            type="text"
            value={formData.color}
            onChange={(e) => handleChange("color", e.target.value)}
            placeholder="f.eks. Sølv"
            className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
              errors.color ? "border-red-600" : "border-gray-300 hover:border-gray-400"
            }`}
          />
          {errors.color && (
            <p className="mt-2 text-sm text-red-600 font-medium">{errors.color}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Registreringsnummer *
        </label>
        <input
          type="text"
          value={formData.licensePlate}
          onChange={(e) => handleChange("licensePlate", e.target.value.toUpperCase())}
          placeholder="f.eks. ABC-1234"
          className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all ${
            errors.licensePlate ? "border-red-600" : "border-gray-300 hover:border-gray-400"
          }`}
        />
        {errors.licensePlate && (
          <p className="mt-2 text-sm text-red-600 font-medium">{errors.licensePlate}</p>
        )}
      </div>

      <div className="flex justify-end pt-6">
        <button
          type="submit"
          className="px-8 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-900 transition-all flex items-center gap-2"
        >
          Fortsett til registreringsdetaljer
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </div>
    </form>
  );
}